import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { authPageHref, safeRedirectPath } from '@/lib/redirect';
import { Loader2, Zap } from 'lucide-react';

export function SignUp() {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Where to land after signing up (e.g. back to an invite link). Same-origin only.
  const redirectPath = safeRedirectPath(searchParams.get('redirect_url'));
  const [name, setName] = useState('');
  const prefillEmail = searchParams.get('email') ?? '';
  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await signUp(email, password, name || undefined);
      navigate(redirectPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Sign up failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm bg-card border border-border rounded-xl p-8 shadow-xl">
        <div className="flex flex-col items-center mb-6">
          <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center mb-3">
            <Zap className="h-5 w-5 text-accent-foreground" />
          </div>
          <h1 className="text-lg font-semibold text-foreground">Create your account</h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              autoComplete="name"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Email</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              autoComplete="email"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">Password</label>
            <input
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-accent"
              autoComplete="new-password"
            />
            <p className="text-[10px] text-muted-foreground mt-1">At least 8 characters.</p>
          </div>

          {error && <p className="text-xs text-destructive">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 py-2.5 bg-accent text-accent-foreground rounded-md text-sm font-semibold hover:bg-accent/90 disabled:opacity-50 transition-colors shadow-kumo-m"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Sign Up
          </button>
        </form>

        <p className="text-xs text-center text-muted-foreground mt-6">
          Already have an account?{' '}
          <Link to={authPageHref('/sign-in', redirectPath, email || prefillEmail)} className="text-accent font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
